import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ASSETS } from '../../assetManifest.js';
import { CONSTANTS } from '../../data.js';
import { calculateStats } from '../../gameLogic/dragonLogic.js';
import { calculateExpansionCost } from '../../gameLogic/shopLogic.js';
import { getRarityColor, getElementColor, getElementGlow } from '../../utils/gameUtils.js';
import { getAssetKey } from '../../utils/assetUtils.js';

const InventoryTab = ({ 
  playerDragons, 
  maxDragons, 
  coins, 
  coinsPerSecond, 
  onExpandInventory, 
  onSellDragon 
}) => {
  const expansionCost = calculateExpansionCost(maxDragons);
  const [hoveredDragonId, setHoveredDragonId] = useState(null);

  if (playerDragons.length === 0) {
    return (
      <div className="card-enhanced h-full flex flex-col">
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <h2 className="text-2xl font-mono font-semibold text-orange-400 text-glow">
              My Dragons ({playerDragons.length}/{maxDragons})
            </h2>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-300 font-sans">
              <span className="text-green-400">+{coinsPerSecond.toLocaleString()}</span> coins/sec
            </div>
            <button 
              onClick={onExpandInventory} 
              disabled={coins < expansionCost} 
              className="btn btn-primary text-xs py-2 px-3"
            >
              Expand (+{CONSTANTS.INVENTORY_EXPANSION_SLOTS_PER_UPGRADE}) - {expansionCost.toLocaleString()}
            </button>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center text-center h-full">
          <div className="opacity-50">
            <div className="text-6xl mb-4">🐉</div>
            <p className="text-gray-400 text-lg font-mono">Your lair is empty.</p>
            <p className="text-gray-500 text-sm mt-2 font-sans">Visit the shop to begin your collection!</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card-enhanced h-full flex flex-col">
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div className="flex items-center space-x-3">
          <h2 className="text-2xl font-mono font-semibold text-orange-400 text-glow">
            My Dragons ({playerDragons.length}/{maxDragons})
          </h2>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-300 font-sans">
            <span className="text-green-400">+{coinsPerSecond.toLocaleString()}</span> coins/sec
          </div>
          <button 
            onClick={onExpandInventory} 
            disabled={coins < expansionCost} 
            className="btn btn-primary text-xs py-2 px-3"
          >
            Expand (+{CONSTANTS.INVENTORY_EXPANSION_SLOTS_PER_UPGRADE}) - {expansionCost.toLocaleString()}
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 -mr-2">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
          {playerDragons.map(dragon => {
            const dragonData = CONSTANTS.DRAGONS[dragon.name];
            const calculatedStats = calculateStats(dragon);
            const coinsPerSec = dragonData.coinsPerSecond * (CONSTANTS.COIN_GENERATION_LEVEL_MULTIPLIER ** (dragon.level - 1));

            return (
              <motion.div
                key={dragon.id}
                layout
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="bg-gray-900/50 p-3 rounded-lg transition-all duration-300 border border-transparent hover:border-white/20 hover:bg-gray-900/80 relative overflow-hidden"
                style={{ '--element-color': getElementColor(dragonData.element) }}
                onMouseEnter={() => setHoveredDragonId(dragon.id)}
                onMouseLeave={() => setHoveredDragonId(null)}
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-[var(--element-color)] opacity-50"></div>
                <div className="text-center mb-2">
                  <div className="relative inline-block mb-2">
                    <img
                      src={ASSETS.dragons[getAssetKey(dragon.name)].path}
                      alt={dragon.name}
                      className="w-16 h-16 transition-all duration-300 transform group-hover:scale-110"
                      style={{ filter: `drop-shadow(0 0 8px ${getElementGlow(dragonData.element)})` }}
                    />
                    <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-black"
                        style={{ backgroundColor: getRarityColor(dragonData.rarity) }}>
                      {dragon.level}
                    </div>
                  </div>
                  <p className="text-sm font-mono font-bold">{dragon.name}</p>
                  <p className="text-xs font-sans" style={{ color: getRarityColor(dragonData.rarity) }}>
                    {dragonData.rarity}
                  </p>
                </div>
                
                <div className="text-xs text-green-400 mb-2 text-center font-sans">
                  +{coinsPerSec.toLocaleString()} coins/sec
                </div>

                <div className="grid grid-cols-3 gap-x-2 gap-y-1 text-xs mt-2 text-gray-400 border-t border-white/10 pt-2 font-sans">
                    <span title="Attack">ATK: {calculatedStats.attack}</span>
                    <span title="Speed">SPD: {calculatedStats.speed}</span>
                    <span title="Defense">DEF: {calculatedStats.defense}</span>
                    <span title="Strength">STR: {calculatedStats.strength}</span>
                    <span title="Physique">PHY: {calculatedStats.physique}</span>
                    <span title="Hidden Power">HPW: {calculatedStats.hiddenPower}</span>
                </div>
                <AnimatePresence>
                {hoveredDragonId === dragon.id && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute bottom-0 left-0 right-0 p-2"
                  >
                    <button
                      onClick={() => onSellDragon(dragon.id)}
                      className="btn btn-danger w-full text-xs px-2 py-2"
                    >
                      Sell ({Math.floor(dragonData.value * CONSTANTS.SELL_PERCENTAGE).toLocaleString()})
                    </button>
                  </motion.div>
                )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default InventoryTab;
